import { memo, useEffect, useState } from 'react';

import { Card, Flex, Form, Select, Steps, Typography } from 'antd';

import equal from 'fast-deep-equal';

import messages from '../../../constants/messages';

import RuleDefinition from './RuleDefinition';
import RuleDetails from './RuleDetails';
import RuleStep from './RuleStep';
import RuleTargetScheduler from './RuleTargetScheduler';
import { TIME_BASED_RULE_TYPES } from './constants';

const { Title, Text } = Typography;

function RuleForm({
  currentStep,
  farms,
  formRef,
  isNew,
  rule,
  types,
  steps,
  onStepChange,
  onChange,
  onFarmChange,
  onRuleChange,
  onTypeChange,
  onTimeBasedChange,
  setSubmittable,
  onTargetsChange
}) {
  const [form] = Form.useForm();

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  const [initialFormValues, setInitialFormValues] = useState({});

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [values]);

  useEffect(() => {
    if (rule?.type) {
      form.setFieldsValue(prepareFormValues());
    }
  }, [rule]);

  const ruleObjects = `${rule?.objects_count} (Livestock: ${rule?.animals_count}, Sites: ${rule?.sites_count}, Pastures: ${rule?.geofences_count})`;
  function prepareFormValues() {
    return {
      id: rule.id,
      name: rule.name,
      description: rule.description,
      objects: ruleObjects,
      farm: rule.farm,
      type: rule.type,
      ...rule.data
    };
  }

  function handleChange() {
    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
  }

  return (
    <Form
      className="mb-2"
      layout="vertical"
      name="ruleBuilderForm"
      form={form}
      requiredMark={true}
      initialValues={prepareFormValues()}
      onValuesChange={handleChange}>
      <Card className="mb-2">
        <Flex vertical={true} className="mb-4">
          <Title level={5}>Rule Creator</Title>
          <Text type="secondary">To create a new rule, follow the steps below. Select only the items you need.</Text>
        </Flex>

        <Steps
          direction="vertical"
          current={currentStep}
          items={[
            {
              title: 'Choose Farm',
              description: (
                <Form.Item name="farm">
                  <Select className="w-full" name="farm" options={farms} value={rule?.farm} onChange={onFarmChange} />
                </Form.Item>
              )
            },
            {
              title: 'Choose Rule Type',
              description: (
                <Form.Item
                  name="type"
                  rules={[
                    {
                      required: true,
                      message: messages.FIELD_REQUIRED('Rule Type')
                    }
                  ]}>
                  <Select
                    className="w-full"
                    name="type"
                    disabled={!rule?.farm}
                    options={types}
                    value={rule?.type}
                    onChange={onTypeChange}
                  />
                </Form.Item>
              )
            },
            {
              title: 'Time-based rule',
              description: (
                <Select
                  className="w-full"
                  name="timeBasedRule"
                  value={rule?.timeBased}
                  disabled={!TIME_BASED_RULE_TYPES.includes(rule.type?.value)}
                  onChange={val => onTimeBasedChange(val)}>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              )
            },
            {
              title: 'Complete Steps',
              description: (
                <>
                  <Flex gap="small" vertical={false} wrap>
                    {steps.map(step => (
                      <RuleStep key={step.key} rule={rule} step={step} onChange={onStepChange} />
                    ))}
                  </Flex>
                  <RuleTargetScheduler
                    rule={rule}
                    targetStep={steps.find(({ key }) => key === 'target')}
                    onTargetsChange={onTargetsChange}
                    isNew={isNew}
                  />
                </>
              )
            }
          ]}
        />

        {rule?.definition && <RuleDefinition definition={rule?.definition} />}
      </Card>

      <RuleDetails isNew={isNew} rule={rule} onRuleChange={onRuleChange} />
    </Form>
  );
}

export default memo(RuleForm);
